import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useAccountAssetLifecyclePolicy,
  useIsAssetLifecycleManagementEnabled,
} from '@frameio/content-security/components/AssetLifecyclePolicy';
import type { CreateWorkspaceInput } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useAccountPermissions } from '@frameio/permissions';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import {
  Button2,
  Dialog,
  DialogFooter,
  DialogHeader,
  Inline,
  Text,
  theme,
} from '@frameio/vapor';
import { IconInfoFill } from '@frameio/vapor-icons';
import {
  type CreateSuccessCallback,
  useCreateWorkspace,
} from '../../../operations/hooks/useCreateWorkspace';
import { DetailsInput } from './components/DetailsInput';
import { InnerDialogWrapper } from './components/StyledComponents';
import TEXT from './generated/TEXT/CreateWorkspaceModal';

export type CreateWorkspaceModalProps = {
  accountId: string;
  isShowing: boolean;
  onRequestClose: () => void;
  onCreateSuccess?: CreateSuccessCallback;
  trackingSource: TrackingSourceType;
};

export function CreateWorkspaceModal({
  accountId,
  isShowing,
  onRequestClose,
  onCreateSuccess,
  trackingSource,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState('');
  const [avatarBlob, setAvatarBlob] = useState<Blob | undefined>(undefined);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const createWorkspace = useCreateWorkspace({
    onSuccess: onCreateSuccess,
  });
  const track = useTrackUnsafe();

  const isSaveDisabled = name.trim() === '';
  const isALMEnabled = useIsAssetLifecycleManagementEnabled();
  const accountPolicy = useAccountAssetLifecyclePolicy(accountId);
  const [lifecycleDays, setLifecycleDays] = useState<number | null>(
    accountPolicy.defaultLifecyclePolicyDays
  );
  const accountCanManageResourceControl = useAccountPermissions({
    accountId,
    check: 'canManageResourceControlPolicies',
  });
  const canChangeAssetLifecycle = accountCanManageResourceControl;

  /**
   * This effect resets the lifecycleDays state when the modal closes, but also
   * sets a default value when creating a new workspace.
   */
  useEffect(() => {
    if (!isShowing) {
      setLifecycleDays(null);
    } else if (isShowing && accountPolicy.defaultLifecyclePolicyDays) {
      setLifecycleDays(accountPolicy.defaultLifecyclePolicyDays);
    }
  }, [isShowing, accountPolicy.defaultLifecyclePolicyDays]);

  const handleCloseDialog = () => {
    setName('');
    setAvatarBlob(undefined);
    onRequestClose();
  };

  const handleSaveWorkspace = () => {
    const input: CreateWorkspaceInput = {
      accountId,
      name,
    };
    if (isALMEnabled && canChangeAssetLifecycle) {
      input.daysBeforeAssetAutoDeletion = lifecycleDays;
    }
    createWorkspace(input, avatarBlob ?? undefined);
    track('workspace-created', { source: trackingSource });
    handleCloseDialog();
  };

  return (
    <Dialog
      data-testid="create-workspace-modal"
      enableClickOutsideToClose={false}
      initialFocusRef={nameInputRef}
      isShowing={isShowing}
      onRequestClose={handleCloseDialog}
      width={DIALOG_WIDTH}
    >
      <DialogHeader closeLabel={TEXT.dialogCloseLabel} title={TEXT.title} />
      <InnerDialogWrapper paddingX={theme.spaces[16]}>
        <DetailsInput
          avatarAlt={name}
          avatarBlob={avatarBlob}
          name={name}
          onAvatarChange={setAvatarBlob}
          onNameChange={setName}
          onSubmit={handleSaveWorkspace}
        />
      </InnerDialogWrapper>
      <DialogFooter>
        <ContentSecurityDisclaimer>
          <Text variant="body">
            <IconInfoFill size={16} />
          </Text>
          <Text variant="caption">{TEXT.contentSecurityDisclaimer}</Text>
        </ContentSecurityDisclaimer>
        <DialogActions>
          <Inline gap={8}>
            <Button2 onPress={handleCloseDialog}>{TEXT.cancel}</Button2>
            <Button2
              data-testid="create-workspace-modal-save-button"
              isDisabled={isSaveDisabled}
              onPress={handleSaveWorkspace}
              variant="primary"
            >
              {TEXT.save}
            </Button2>
          </Inline>
        </DialogActions>
      </DialogFooter>
    </Dialog>
  );
}

export const DIALOG_WIDTH = '680px';

const DialogActions = styled.div`
  flex-shrink: 0;
  margin-left: auto;
`;

const ContentSecurityDisclaimer = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 1;
  gap: ${theme.spaces[12]};
  margin-right: ${theme.spaces[24]};
`;

TEXT.DEFINE(
  {
    /** Placeholder text for workspace name input */
    addWorkspaceName: 'Add workspace name',
    /** Secondary action label to cancel workspace creation */
    cancel: 'Cancel',
    /** Dialog title for creating a workspace */
    title: 'Create New Workspace',
    /** Label for the avatar editor action */
    editAvatar: 'Edit workspace avatar',
    /** Primary action label to save the new workspace */
    save: 'Save',
    /** Label for the workspace name field */
    workspaceName: 'Workspace name',
    /** Accessible label for the dialog close button */
    dialogCloseLabel: 'Close Dialog',
    /** Text labels for asset lifecycle defaults on create */
    assetLifecyclePolicy: {
      /** Description when asset lifecycle defaults are disabled */
      disabledDescription: 'Auto-delete assets after upload date.',
      /** Description when asset lifecycle defaults are enabled */
      enabledDescription: (days: number) =>
        plural(days, {
          one: 'Auto-delete assets 1 day after upload date.',
          other: `Auto-delete assets ${days} days after upload date.`,
        }),
      /** Inline alert message explaining default policy impact */
      inlineAlert: (days: number) =>
        plural(days, {
          one: 'Assets uploaded more than 1 day ago will be deleted within 1 day.',
          other: `Assets uploaded more than ${days} days ago will be deleted within 1 day.`,
        }),
      /** Setting label for asset expiration defaults */
      settingsLabel: 'Asset Expiration',
    },
    /** Disclaimer about inherited content security defaults */
    contentSecurityDisclaimer:
      'Workspace content security settings will inherit the defaults from your account settings. Update them in workspace settings after the workspace is created.',
  },
  { plural }
);
